import { Box, FormControlLabel, Checkbox } from '@mui/material';
import { useCallback, useEffect, useMemo, memo, useState, useRef } from 'react';
import { AutomationDataActionType, AutomationTypes } from '../../types/enums/AutomationTypes';
import StyledTooltip from '../common/StyledTooltip';
import { AutomationActionType } from '../../types/enums/AutomationActionType';
import { AutomationsCreateEditDialogHeader } from '../common/create-edit-dialog/AutomationsCreateEditDialogHeader';
import { useIsPristine } from '../../hooks/useIsPristine';
import { Typography, colors, IconButton } from '@sweep-io/sweep-design';
import usePermission from '../common/permissions/usePermission';
import AlertPermission from '../common/AlertPermission';
import { AutomationActionPopupMenu } from './AutomationActionPopupMenu';
import { CustomHeader } from '../common/rule-builder/CustomHeader';
import { SweepFieldsRuleBuilder } from '../common/sweep-condition-rule-builder/SweepConditionRuleBuilder';
import { useRefForMultipleRuleBuilders } from '../common/sweep-condition-rule-builder/MultipleRuleBuildersContext';
import { SelectorValueTypes } from '../../types/enums/SelectorValueTypes';
import { TimeSelector } from './Triggers/TimeSelector';
import FieldLabel from '../common/FieldLabel';
import { Delete as DeleteListIcon } from '@sweep-io/sweep-design/dist/icons';
import { ObjectTypeLabelAndKeyTag } from '../common/ObjectTypeLabelAndKey';
import { AutomationFormVariant } from '../../types/enums/AutomationFormVariant';
import useObjectTypesWithFetch from '../../hooks/useObjectTypesWithFetch';
import { AUTOMATIONS_LAYOUT_MAX_WIDTH } from './helper';
import { CenteredCircularProgress } from '../common/CenteredCircularProgress';
import { AutomationDefaultTriggers } from './AutomationDefaultTriggers';
import { AutomationScheduledAssignmentTriggers } from './AutomationScheduledAssignmentTriggers';
import { SalesforceReportSelector } from './SalesforceReportSelector';
import { useSelector } from 'react-redux';
import { AutomationType } from '../../types/enums/AutomationType';
import { AutomationsActionsWrapper } from './AutomationsActionsWrapper';
import { useAutomationsContext } from './AutomationsContext';
import { AutomationPermissionModeSelector } from './AutomationPermissionModeSelector';
import { AutomationUserSettings } from './AutomationUserSettings';
import { selectCrmOrg } from '../pages/environments/environmentsReducer';
import { MarketingAttributionForm } from './marketing-attribution-form/MarketingAttributionForm';
import { ConditionsWrapper } from './ConditionsWrapper';
import { useSetQueryParamsOnMount } from '../../navigation-utils/useSetQueryParamsOnMount';
import { DeepLinkSearchParams } from '@server/deep-link-search-params';
import { getMarketingAttributionAction } from './marketing-attribution-form/helpers';
import { readableUUID } from '../../navigation-utils/readableUUID';
import { validateTrigger } from './automation-form-validation/automationFormValidationHelper';
import { SFDCObjectType } from '../../types/enums/SFDCObjectType';
import {
  MARKETING_ATTRIBUTION_FILTERS,
  MARKETING_ATTRIBUTION_DISPLAY_NAMES,
} from './marketing-attribution-form/helpers';
import { SfdcDisplayType } from '../../types/fields';

export const labelBoxDesign = {
  width: 'fit-content',
  borderRadius: '4px',
  height: '24px',
  marginBottom: '8px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  p: '0 8px',
};

interface AutomationFormProps {
  onBackButtonClick: () => any;
  crmOrgId: string;
  onPristineChange: (pristine: boolean) => any;
  readonly?: boolean;
  objectName: ObjectTypeValues;
  recordTypesAndFunnelsDataForObject?: ParsedRecordTypesAndFunnelsObjectData;
  filterFunnelIds?: string[];
  isDoneLoading: boolean;
}

export type PartialAutomationStructure = DeepPartial<AutomationStructureNew>;

function helperShowFieldContextMenu(
  automationVariant: AutomationFormVariant,
  automationJson: DeepPartial<AutomationStructureNew>,
): boolean | undefined {
  return (
    automationVariant !== AutomationFormVariant.SCHEDULED_ASSIGNMENTS &&
    (automationJson.automationDetails?.triggerType === AutomationTypes.OnUpdate ||
      automationJson.automationDetails?.triggerType === AutomationTypes.FieldUpdate)
  );
}

const AutomationsForm = memo(
  ({
    onBackButtonClick,
    crmOrgId,
    onPristineChange,
    readonly = false,
    objectName,
    recordTypesAndFunnelsDataForObject,
    filterFunnelIds,
    isDoneLoading,
  }: AutomationFormProps) => {
    const dataByVariant = useAutomationsContext();
    const { automationJson, setAutomationJson } = dataByVariant;

    const [headerValue, setHeaderValue] = useState(automationJson.name || '');

    useEffect(() => {
      setHeaderValue(automationJson.name || '');
    }, [automationJson.name]);

    const timeoutRef = useRef<NodeJS.Timeout>();
    const automationJsonRef = useRef(automationJson);
    const setAutomationJsonRef = useRef(setAutomationJson);

    useEffect(() => {
      automationJsonRef.current = automationJson;
    }, [automationJson]);

    useEffect(() => {
      setAutomationJsonRef.current = setAutomationJson;
    }, [setAutomationJson]);

    const debouncedSetAutomationName = useCallback((name: string) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        if (automationJsonRef.current.name !== name) {
          setAutomationJsonRef.current({
            ...automationJsonRef.current,
            name,
          });
        }
      }, 300);
    }, []);

    const handleHeaderChange = useCallback(
      (value: string) => {
        setHeaderValue(value);
        debouncedSetAutomationName(value);
      },
      [debouncedSetAutomationName],
    );

    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    const bypassEntirely = useMemo(
      () => !Boolean(automationJson?.automationId),
      [automationJson?.automationId],
    );

    useSetQueryParamsOnMount(
      {
        [DeepLinkSearchParams.automationId]: readableUUID(automationJson?.automationId),
        [DeepLinkSearchParams.versionId]: readableUUID(automationJson?.versionId),
        [DeepLinkSearchParams.automationType]: automationJson?.type,
      },
      bypassEntirely,
    );

    const { automationVariant, permissions, isObjectless } = dataByVariant;

    const isPlaybookAlerts = useMemo(
      () => automationVariant === AutomationFormVariant.PLAYBOOK_ALERT,
      [automationVariant],
    );

    const isScheduledAssignment = useMemo(
      () => automationVariant === AutomationFormVariant.SCHEDULED_ASSIGNMENTS,
      [automationVariant],
    );

    const isScheduledType = useMemo(
      () =>
        automationVariant === AutomationFormVariant.SCHEDULED_REPORTS ||
        automationVariant === AutomationFormVariant.SCHEDULED_JOBS,
      [automationVariant],
    );

    const isMarketingAttribution = useMemo(
      () => automationVariant === AutomationFormVariant.MARKETING_ATTRIBUTIONS,
      [automationVariant],
    );

    const isShowFollowUpAction = useMemo(
      () => !(isPlaybookAlerts || isScheduledType),
      [isPlaybookAlerts, isScheduledType],
    );

    const [isAllowedBtn] = usePermission([permissions.edit, permissions.deploy]);
    if (!isAllowedBtn) {
      readonly = true;
    }

    let funnelsList = recordTypesAndFunnelsDataForObject?.funnels;
    if (funnelsList && filterFunnelIds) {
      funnelsList = funnelsList.filter((funnel) => filterFunnelIds.includes(funnel.id));
    }

    const { objectTypes } = useObjectTypesWithFetch({ crmOrgId });
    const crmOrg = useSelector(selectCrmOrg(crmOrgId));

    const isPristine = useIsPristine(automationJson);

    useEffect(() => {
      onPristineChange(isPristine);
    }, [isPristine, onPristineChange]);

    const ruleBuilderRef = useRefForMultipleRuleBuilders();

    const _onChange = useCallback(
      async (action: DeepPartial<AutomationAction>, idx?: number) => {
        const temp = [...(automationJson?.automationDetails?.actions || [])];
        if (temp) {
          if (idx !== undefined) {
            temp[idx] = action;
          } else {
            temp.push(action);
          }

          setAutomationJson({
            ...automationJson,
            automationDetails: {
              ...automationJson.automationDetails,
              actions: temp,
            },
          });
        }
      },
      [automationJson, setAutomationJson],
    );

    const removeConditionsToTrigger = () => {
      setAutomationJson({
        ...automationJson,
        automationDetails: {
          ...automationJson.automationDetails,
          when: undefined,
        },
      });
    };

    const removeMarketingAttributionConditions = (filterTypeToRemove: string) => {
      const actions = automationJson.automationDetails
        ?.actions as MarketingAttributionModelAction[];
      const action = actions[0];
      setAutomationJson({
        ...automationJson,
        automationDetails: {
          ...automationJson.automationDetails,
          actions: [
            {
              ...action,
              actionParams: {
                ...action?.actionParams,
                [filterTypeToRemove]: undefined,
              },
            },
          ],
        },
      });
    };

    const removeDelayAction = () => {
      setAutomationJson({
        ...automationJson,
        automationDetails: {
          ...automationJson.automationDetails,
          delay: undefined,
        },
      });
    };
    const addScheduledDataAction = (newSweepCondition: SweepCondition) => {
      setAutomationJson({
        ...automationJson,
        automationDetails: {
          ...automationJson.automationDetails,
          getDataAction: {
            ...automationJson.automationDetails?.getDataAction,
            ...{
              actionType: AutomationDataActionType.QUERY_RESULTS,
              actionParams: {
                criteria: newSweepCondition,
              },
            },
          } as AutomationDataActionQueryResult,
        },
      });
    };

    const { criteria, sweepCondition } = useMemo(() => {
      const criteria = automationJson.automationDetails?.when?.criteria;

      const sweepCondition: SweepCondition | undefined = criteria
        ? {
            criteria: criteria.criteria,
            criteriaLogic: criteria.criteriaLogic,
          }
        : undefined;

      return { criteria, sweepCondition };
    }, [automationJson.automationDetails?.when?.criteria]);

    const excludeValueType = useCallback(
      (type: SelectorValueTypes) => {
        if (type === SelectorValueTypes.PRIOR) {
          const currType = automationJson.automationDetails?.triggerType;
          if (
            currType === AutomationTypes.OnCreate ||
            currType === AutomationTypes.DateArrive ||
            currType === AutomationTypes.TimeInStep ||
            currType === AutomationTypes.Scheduled
          ) {
            return true;
          }
          // exclude Prior value if automation has Delay
          if (automationJson.automationDetails?.delay) {
            return true;
          }
        }
        return false;
      },
      [automationJson.automationDetails?.delay, automationJson.automationDetails?.triggerType],
    );

    const onClearAction = useCallback(
      (idx: number) => {
        const temp = [...(automationJson?.automationDetails?.actions || [])];
        temp?.splice(idx, 1);
        setAutomationJson({
          ...automationJson,
          automationDetails: {
            ...automationJson.automationDetails,
            actions: temp,
          },
        });
      },
      [automationJson, setAutomationJson],
    );

    const onAddAction = useCallback(
      (emptyAction: Partial<AutomationAction>, isBefore: boolean, idx: number) => {
        const temp = [...(automationJson?.automationDetails?.actions || [])];
        if (isBefore) {
          temp?.splice(idx, 0, emptyAction);
        } else {
          temp?.splice(idx + 1, 0, emptyAction);
        }

        setAutomationJson({
          ...automationJson,
          automationDetails: {
            ...automationJson.automationDetails,
            actions: temp,
          },
        });
      },
      [automationJson, setAutomationJson],
    );

    const isTriggerScheduled = useMemo(
      () =>
        automationJson?.automationDetails?.triggerType === AutomationTypes.Scheduled &&
        automationJson?.type !== AutomationType.ScheduledAssignment &&
        automationJson?.type !== AutomationType.ScheduledJob &&
        automationJson?.type !== AutomationType.ScheduledReport,
      [automationJson?.automationDetails?.triggerType, automationJson?.type],
    );

    const isHideActionSelector = useMemo(
      () =>
        automationJson?.type === AutomationType.ScheduledReport ||
        automationJson?.type === AutomationType.ScheduledJob,
      [automationJson?.type],
    );

    const getScheduledDataCriteria = useMemo(
      () =>
        (automationJson?.automationDetails?.getDataAction as AutomationDataActionQueryResult)
          ?.actionParams?.criteria,
      [automationJson?.automationDetails?.getDataAction],
    );

    const isShowOrder = useMemo(
      () =>
        automationJson?.type === AutomationType.Default ||
        automationJson?.type === AutomationType.Alert ||
        automationJson?.type === AutomationType.Assignment,
      [automationJson?.type],
    );

    const _rightSidePermissionContent = useMemo(() => {
      return isShowOrder ? (
        <AutomationUserSettings crmOrgId={crmOrgId} readonly={readonly} />
      ) : (
        <AutomationPermissionModeSelector />
      );
    }, [isShowOrder, crmOrgId, readonly]);

    const _objectTypeChip = useMemo(() => {
      if (isObjectless) return <></>;
      return (
        <ObjectTypeLabelAndKeyTag
          objectType={objectName}
          label={recordTypesAndFunnelsDataForObject?.label || objectName}
          tagVariant="medium"
        />
      );
    }, [isObjectless, objectName, recordTypesAndFunnelsDataForObject?.label]);

    const _rightSideContent = useMemo(
      () => <Box sx={{ ml: '100px' }}>{_objectTypeChip}</Box>,
      [_objectTypeChip],
    );

    if (!isDoneLoading) {
      return <CenteredCircularProgress />;
    }

    return (
      <Box>
        <AutomationsCreateEditDialogHeader
          showCancelButton
          onCancelClick={onBackButtonClick}
          rightSideContent={_rightSideContent}
          isStickyHeader
          sx={{
            background: colors.white,
            paddingTop: '20px',
            paddingBottom: '20px',
            gap: '16px',
          }}
          hideBackButton={true}
          onChange={handleHeaderChange}
          placeholder={dataByVariant.placeholder}
          value={headerValue}
          editDisabled={!isAllowedBtn || readonly}
          rightSidePermissionContent={_rightSidePermissionContent}
          errorHelperText={!headerValue ? 'Name is required' : ''}
        />
        <Box
          sx={{
            maxHeight: 'calc(100% - 95px)',
            overflowY: 'auto',
            maxWidth: AUTOMATIONS_LAYOUT_MAX_WIDTH,
          }}
        >
          {!isAllowedBtn && (
            <AlertPermission
              alertMessage={`You don't have permission to edit ${dataByVariant.nameInMessage} in environment ${crmOrg?.name}`}
              addedStyle={{
                width: '470px',
                position: 'absolute',
                right: '40px',
                top: '98px',
                zIndex: '1000',
              }}
            />
          )}
          {!isScheduledAssignment && !isMarketingAttribution && (
            <AutomationDefaultTriggers
              automationJson={automationJson}
              objectName={objectName}
              callBack={(data: DeepPartial<AutomationStructureNew>) => {
                setAutomationJson(data);
              }}
              crmOrgId={crmOrgId}
              readonly={readonly}
              recordTypesAndFunnelsDataForObject={recordTypesAndFunnelsDataForObject}
              filterFunnelIds={filterFunnelIds}
            />
          )}
          {isScheduledAssignment && (
            <AutomationScheduledAssignmentTriggers
              automationJson={automationJson}
              objectName={objectName}
              callBack={(data: DeepPartial<AutomationStructureNew>) => {
                setAutomationJson(data);
              }}
            />
          )}

          {isMarketingAttribution && (
            <MarketingAttributionForm
              removeConditionsToTrigger={removeConditionsToTrigger}
              removeMarketingAttributionConditions={removeMarketingAttributionConditions}
              helperShowFieldContextMenu={helperShowFieldContextMenu}
              setAutomationJson={setAutomationJson}
              crmOrgId={crmOrgId}
              automationVariant={automationVariant}
              onChange={(data: DeepPartial<AutomationStructureNew>) => {
                setAutomationJson(data);
              }}
              readonly={readonly}
              model={automationJson}
              setDisplayObject={(object: string) => {
                const attributionAction = getMarketingAttributionAction(automationJson);

                const filterPropertyMap: Record<string, string> = {
                  [MARKETING_ATTRIBUTION_DISPLAY_NAMES.CAMPAIGN]:
                    MARKETING_ATTRIBUTION_FILTERS.CAMPAIGN,
                  [MARKETING_ATTRIBUTION_DISPLAY_NAMES.CAMPAIGN_MEMBER]:
                    MARKETING_ATTRIBUTION_FILTERS.CAMPAIGN_MEMBER,
                  [MARKETING_ATTRIBUTION_DISPLAY_NAMES.OPPORTUNITY_CONTACT_ROLE]:
                    MARKETING_ATTRIBUTION_FILTERS.OPPORTUNITY_CONTACT_ROLE,
                };

                if (object === SFDCObjectType.Opportunity) {
                  setAutomationJson({
                    ...automationJson,
                    automationDetails: {
                      ...automationJson.automationDetails,
                      when: {
                        ...automationJson.automationDetails?.when,
                        criteria: {
                          criteria: [],
                          criteriaLogic: '',
                        },
                        isCriteria: true,
                      },
                    },
                  });
                } else if (filterPropertyMap[object]) {
                  const filterProperty = filterPropertyMap[object];
                  setAutomationJson({
                    ...automationJson,
                    automationDetails: {
                      ...automationJson.automationDetails,
                      actions: [
                        {
                          ...attributionAction,
                          actionParams: {
                            ...attributionAction?.actionParams,
                            [filterProperty]: {
                              criteria: [],
                              criteriaLogic: '',
                              isCriteria: true,
                            },
                          },
                        },
                      ],
                    },
                  });
                }
              }}
            />
          )}

          {automationJson.automationDetails?.when?.isCriteria &&
            automationJson.objectName &&
            !isMarketingAttribution && (
              <ConditionsWrapper
                removeConditionsToTrigger={removeConditionsToTrigger}
                customHeader={'When these criteria are met:'}
                ruleBuilderProps={{
                  readOnly: readonly,
                  crmOrgId: crmOrgId ?? '',
                  objectType: automationJson.objectName,
                  sweepCondition: sweepCondition,
                  onChange: (newSweepCondition) => {
                    const _criteria = criteria || {
                      criteria: [],
                      criteriaLogic: '',
                    };
                    const temp: CriteriaDetails = {
                      ...automationJson.automationDetails?.when,
                      criteria: { ..._criteria, ...newSweepCondition },
                    };
                    setAutomationJson({
                      ...automationJson,
                      automationDetails: {
                        ...automationJson.automationDetails,
                        when: temp,
                      },
                    });
                  },
                  showFieldContextMenu: helperShowFieldContextMenu(
                    automationVariant,
                    automationJson,
                  ),
                }}
              />
            )}

          {automationJson.automationDetails?.delay &&
            automationJson.objectName &&
            !isMarketingAttribution && (
              <>
                <Box sx={{ marginBottom: '16px' }}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      flexDirection: 'row',
                      alignItems: 'flex-start',
                      gap: '17px',
                      height: '24px',
                      mb: '8px',
                      '&:hover .automationRemoveDelay': {
                        display: 'block',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        ...labelBoxDesign,
                        background: colors.seafoam[500],
                      }}
                    >
                      <Typography variant="caption-bold" textAlign="center" color={colors.white}>
                        WAIT
                      </Typography>
                    </Box>
                    <Box className="automationRemoveDelay" sx={{ display: 'none' }}>
                      <StyledTooltip title="Remove delay" placement="top">
                        <span>
                          <IconButton variant="flat" size="tiny" onClick={removeDelayAction}>
                            <DeleteListIcon />
                          </IconButton>
                        </span>
                      </StyledTooltip>
                    </Box>
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                      <TimeSelector
                        onChangeValue={(val) => {
                          const _delay = {
                            ...automationJson.automationDetails?.delay,
                            offsetValue: val,
                          };
                          setAutomationJson({
                            ...automationJson,
                            automationDetails: {
                              ...automationJson.automationDetails,
                              delay: _delay,
                            },
                          });
                        }}
                        onChangeValueType={(val) => {
                          const _delay = {
                            ...automationJson.automationDetails?.delay,
                            offsetType: val,
                          };
                          setAutomationJson({
                            ...automationJson,
                            automationDetails: {
                              ...automationJson.automationDetails,
                              delay: _delay,
                            },
                          });
                        }}
                        object={automationJson.automationDetails?.delay || {}}
                        intervalsArray={['Minutes', 'Hours', 'Days']}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        marginTop: '8px',
                        '& label': { paddingBottom: 0 },
                      }}
                    >
                      <FormControlLabel
                        label={
                          <FieldLabel
                            label={
                              'Continue to actions only if the trigger and conditions are still valid'
                            }
                          />
                        }
                        control={
                          <Checkbox
                            disabled={readonly}
                            checked={
                              automationJson.automationDetails?.delay?.shouldReevaluate ?? false
                            }
                            onChange={(value) => {
                              const isChecked = value.target.checked;
                              const _delay = {
                                ...automationJson.automationDetails?.delay,
                                shouldReevaluate: isChecked,
                              };
                              setAutomationJson({
                                ...automationJson,
                                automationDetails: {
                                  ...automationJson.automationDetails,
                                  delay: _delay,
                                },
                              });
                            }}
                          />
                        }
                      />
                    </Box>
                  </Box>
                </Box>
              </>
            )}

          {isTriggerScheduled && (
            <Box
              sx={{
                backgroundColor: colors.grey[100],
                paddingTop: '12px',
                borderRadius: '6px',
                mb: 2,
              }}
            >
              <>
                <CustomHeader
                  customHeader={`For every ${recordTypesAndFunnelsDataForObject?.label || objectName} record that meet the following conditions`}
                />
                <SweepFieldsRuleBuilder
                  ref={ruleBuilderRef}
                  readOnly={readonly}
                  crmOrgId={crmOrgId ?? ''}
                  objectType={objectName}
                  sweepCondition={getScheduledDataCriteria}
                  onChange={addScheduledDataAction}
                  hideSelectValuesFromRecord={true}
                  nestedSelectorFilterBy={(field) =>
                    field.sfDisplayType !== SfdcDisplayType.LONGTEXTAREA
                  }
                  disableResolvePolymorphic
                />
              </>
            </Box>
          )}

          <Box>
            <Box sx={{ marginBottom: '16px' }}>
              {!isMarketingAttribution && (
                <Box sx={{ display: 'flex', gap: '8px' }}>
                  <Box
                    sx={{
                      ...labelBoxDesign,
                      background: isPlaybookAlerts ? colors.lemon[500] : colors.gum[500],
                    }}
                  >
                    <Typography
                      variant="caption-bold"
                      textAlign="center"
                      color={isPlaybookAlerts ? colors.grey[900] : colors.white}
                    >
                      THEN
                    </Typography>
                  </Box>
                  {isShowFollowUpAction && (
                    <AutomationActionPopupMenu
                      isOnlyAfter={isScheduledAssignment}
                      readonly={readonly}
                      action={
                        automationJson.automationDetails?.actions &&
                        automationJson.automationDetails?.actions[0]
                      }
                      onClearAction={() => onClearAction(0)}
                      onAddAction={(emptyAction, isBefore) => onAddAction(emptyAction, isBefore, 0)}
                      isClearable={
                        automationJson.automationDetails?.actions &&
                        automationJson.automationDetails?.actions[0]?.actionType !==
                          AutomationActionType.AssignmentRules
                      }
                    />
                  )}
                </Box>
              )}

              {isScheduledAssignment && (
                <SalesforceReportSelector
                  automationJson={automationJson}
                  readonly={readonly}
                  crmOrgId={crmOrgId}
                  callBack={(data: DeepPartial<AutomationStructureNew>) => {
                    setAutomationJson(data);
                  }}
                />
              )}

              {!isMarketingAttribution && (
                <AutomationsActionsWrapper
                  isHideActionSelector={isHideActionSelector}
                  onAddAction={onAddAction}
                  onClearAction={onClearAction}
                  onChange={_onChange}
                  objectName={automationJson.objectName || ''}
                  readonly={readonly}
                  actionList={
                    (automationJson.automationDetails?.actions as AutomationAction[]) || []
                  }
                  excludeValueType={excludeValueType}
                  isPlaybookAlerts={isPlaybookAlerts}
                  objectTypes={objectTypes}
                  crmOrgId={crmOrgId}
                  isActionEnabled={validateTrigger(automationJson, isObjectless)}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  },
);

export { AutomationsForm };
